
import { AlertCircle, CheckCircle2, Clock3, FileWarning, Loader2, RefreshCw, TriangleAlert } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { useToast } from "@/lib/hooks/use-toast"
import { readStoredA1Certificate } from "@/features/products/components/previdenciario/certificado-upload"
import {
  type EsocialRetornoEventoDetalhadoResponse,
  type EsocialRetornoLoteResponse,
  useReconsultarPendentesEsocial,
} from "@/features/products/services"

interface ESocialRetornosPanelProps {
  idPrevidenciario?: string
  lotes: EsocialRetornoLoteResponse[]
}

function toNonEmptyString(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value)
  }

  return null
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return "-"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("pt-BR")
}

function extractBaseFileName(path: string | null | undefined): string | null {
  const filePath = toNonEmptyString(path)
  if (!filePath) {
    return null
  }

  const normalized = filePath.replace(/\\/g, "/")
  return normalized.split("/").filter(Boolean).pop() ?? normalized
}

function getLoteProcessamentoCode(lote: EsocialRetornoLoteResponse): string | null {
  const processResumo = lote.resumos.find((resumo) => resumo.etapa === "PROCESSAMENTO")
  return lote.codigoRespostaProcessamento ?? processResumo?.codigoResposta ?? null
}

function getLoteProcessamentoDescription(lote: EsocialRetornoLoteResponse): string | null {
  const processResumo = lote.resumos.find((resumo) => resumo.etapa === "PROCESSAMENTO")
  return lote.descricaoRespostaProcessamento ?? processResumo?.descricaoResposta ?? null
}

function isLotePending(lote: EsocialRetornoLoteResponse): boolean {
  return getLoteProcessamentoCode(lote) === "101" || lote.status === "PENDENTE"
}

function getEventFileName(
  lote: EsocialRetornoLoteResponse,
  evento: EsocialRetornoEventoDetalhadoResponse
): string | null {
  const fileFromEvento = extractBaseFileName(evento.arquivo)
  if (fileFromEvento) {
    return fileFromEvento
  }

  const payloadMatch = lote.payloadEventoDetalhado?.find((item) => toNonEmptyString(item.id) === evento.eventoId)
  return extractBaseFileName(toNonEmptyString(payloadMatch?.arquivo))
}

function getEventStatusTone(evento: EsocialRetornoEventoDetalhadoResponse): "success" | "warning" | "destructive" | "secondary" {
  const code = evento.codigoRespostaProcessamento

  if (code === "201" || evento.statusProcessamento === "SUCESSO") {
    return "success"
  }

  if (code === "202" || evento.statusProcessamento === "SUCESSO_COM_ADVERTENCIA") {
    return "warning"
  }

  if (code === "101" || evento.statusProcessamento === "PENDENTE") {
    return "secondary"
  }

  return "destructive"
}

function PendingFilesList({ lote }: { lote: EsocialRetornoLoteResponse }) {
  const arquivos = (lote.payloadEventoDetalhado ?? [])
    .map((item) => extractBaseFileName(toNonEmptyString(item.arquivo)))
    .filter((item): item is string => Boolean(item))

  if (!arquivos.length) {
    return null
  }

  return (
    <div className="rounded-lg border bg-muted/30 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Arquivos aguardando retorno
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {arquivos.map((arquivo) => (
          <Badge key={arquivo} variant="outline" className="font-mono text-[11px]">
            {arquivo}
          </Badge>
        ))}
      </div>
    </div>
  )
}

export function ESocialRetornosPanel({ idPrevidenciario, lotes }: ESocialRetornosPanelProps) {
  const { toast } = useToast()
  const reconsultarPendentes = useReconsultarPendentesEsocial(idPrevidenciario)

  const lotesPendentes = lotes.filter(isLotePending)
  const totalEventos = lotes.reduce((sum, lote) => sum + lote.totalEventos, 0)
  const totalErros = lotes.reduce((sum, lote) => sum + lote.totalErros, 0)
  const totalAdvertencias = lotes.reduce((sum, lote) => sum + lote.totalAdvertencias, 0)

  const handleReconsultarPendentes = async () => {
    const certInfo = readStoredA1Certificate()

    if (!certInfo?.certFile || !certInfo.certPassword.trim()) {
      toast({
        title: "Certificado obrigatório",
        description: "Adicione o certificado A1 (.pfx) e a senha antes de reconsultar os lotes.",
        variant: "destructive",
      })
      return
    }

    try {

      const result = await reconsultarPendentes.mutateAsync({
        certFile: certInfo.certFile,
        certPassword: certInfo.certPassword,
      })

      toast({
        title: "Reconsulta concluída",
        description:
          result.data.totalProtocolos > 0
            ? `${result.data.totalProtocolos} protocolo(s) enviados para nova consulta.`
            : result.message,
      })
    } catch (error) {
      toast({
        title: "Erro ao reconsultar",
        description: error instanceof Error ? error.message : "Falha ao reconsultar lotes pendentes.",
        variant: "destructive",
      })
    }
  }

  if (!lotes.length) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-card px-6 py-10 text-center text-muted-foreground">
        <FileWarning className="mb-3 h-8 w-8" />
        <p className="text-sm font-medium text-foreground">Nenhum status de envio salvo ainda</p>
        <p className="mt-1 text-xs">
          Quando houver consulta de lote, os dados de esocial_retorno_lotes aparecerão aqui.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">Status dos envios eSocial</p>
          <p className="text-xs text-muted-foreground">
            Informações vindas de esocial_retorno_lotes. A reconsulta manual envia o mesmo certificado A1 salvo no fluxo e substitui entradas idênticas do histórico local.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleReconsultarPendentes}
          disabled={reconsultarPendentes.isPending || !idPrevidenciario}
          className="gap-2"
        >
          {reconsultarPendentes.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Reconsultar lotes
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[10px] uppercase text-muted-foreground">Lotes</p>
          <p className="text-lg font-bold">{lotes.length}</p>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[10px] uppercase text-muted-foreground">Eventos</p>
          <p className="text-lg font-bold">{totalEventos}</p>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[10px] uppercase text-muted-foreground">Pendentes 101</p>
          <p className="text-lg font-bold">{lotesPendentes.length}</p>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[10px] uppercase text-muted-foreground">Erros / Avisos</p>
          <p className="text-lg font-bold">
            {totalErros} / {totalAdvertencias}
          </p>
        </div>
      </div>

      {lotesPendentes.length > 0 && (
        <Alert variant="warning" className="border-warning/30 bg-warning/10 text-foreground">
          <Clock3 className="h-4 w-4" />
          <AlertTitle>Lotes ainda em processamento</AlertTitle>
          <AlertDescription>
            <span>
              Existem {lotesPendentes.length} lote(s) com código <strong>101</strong>. Eles precisam ser consultados novamente
              depois para trazer o resultado final.
            </span>
          </AlertDescription>
        </Alert>
      )}

      <Accordion type="single" collapsible className="rounded-lg border bg-card px-4">
        {lotes.map((lote, index) => {
          const code = getLoteProcessamentoCode(lote)
          const description = getLoteProcessamentoDescription(lote)
          const pending = isLotePending(lote)

          return (
            <AccordionItem key={lote.id} value={lote.id}>
              <AccordionTrigger className="hover:no-underline">
                <div className="flex w-full flex-col gap-3 pr-4 text-left md:flex-row md:items-center md:justify-between">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">
                        Lote {String(index + 1).padStart(2, "0")}
                      </span>
                      {lote.grupo && <Badge variant="outline">{lote.grupo}</Badge>}
                      {pending && <Badge variant="outline">Aguardando processamento</Badge>}
                    </div>
                    <p className="font-mono text-xs text-muted-foreground">{lote.protocoloEnvio ?? "Sem protocolo"}</p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant="outline"
                      className={cn(
                        pending && "border-warning/40 bg-warning/10 text-warning",
                        !pending && lote.totalErros > 0 && "border-destructive/40 bg-destructive/10 text-destructive",
                        !pending &&
                          lote.totalErros === 0 &&
                          lote.totalAdvertencias > 0 &&
                          "border-warning/40 bg-warning/10 text-warning",
                        !pending &&
                          lote.totalErros === 0 &&
                          lote.totalAdvertencias === 0 &&
                          "border-success/40 bg-success/10 text-success"
                      )}
                    >
                      {code ?? lote.status ?? "SEM STATUS"}
                    </Badge>
                    <Badge variant="outline">{lote.totalEventos} evento(s)</Badge>
                    <Badge variant="outline">{lote.totalErros} erro(s)</Badge>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className="space-y-4">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-lg border bg-muted/20 p-3">
                    <p className="text-[10px] uppercase text-muted-foreground">Código</p>
                    <p className="mt-1 font-semibold">{code ?? "-"}</p>
                  </div>
                  <div className="rounded-lg border bg-muted/20 p-3">
                    <p className="text-[10px] uppercase text-muted-foreground">Recepção</p>
                    <p className="mt-1 font-semibold">{formatDateTime(lote.dhRecepcao)}</p>
                  </div>
                  <div className="rounded-lg border bg-muted/20 p-3">
                    <p className="text-[10px] uppercase text-muted-foreground">Processados</p>
                    <p className="mt-1 font-semibold">
                      {lote.totalEventosProcessados} / {lote.totalEventos}
                    </p>
                  </div>
                  <div className="rounded-lg border bg-muted/20 p-3">
                    <p className="text-[10px] uppercase text-muted-foreground">Versão recepção</p>
                    <p className="mt-1 font-semibold">{lote.versaoAplicativoRecepcao ?? "-"}</p>
                  </div>
                </div>

                {description && (
                  <Alert variant={pending ? "warning" : lote.totalErros > 0 ? "destructive" : "info"}>
                    {pending ? <Clock3 className="h-4 w-4" /> : lote.totalErros > 0 ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                    <AlertTitle>Resumo do lote</AlertTitle>
                    <AlertDescription>{description}</AlertDescription>
                  </Alert>
                )}

                {lote.eventosDetalhados.length === 0 ? (
                  <PendingFilesList lote={lote} />
                ) : (
                  <div className="rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Arquivo</TableHead>
                          <TableHead>ID do evento</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Recibo</TableHead>
                          <TableHead>Processado em</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lote.eventosDetalhados.map((evento) => {
                          const fileName = getEventFileName(lote, evento)
                          const statusTone = getEventStatusTone(evento)

                          return (
                            <TableRow key={evento.id}>
                              <TableCell>
                                <div className="space-y-1">
                                  <p className="font-medium text-foreground">{fileName ?? "Arquivo não identificado"}</p>
                                  {evento.arquivo && (
                                    <p className="max-w-[320px] truncate text-[11px] text-muted-foreground">{evento.arquivo}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  <p className="font-mono text-xs">{evento.eventoId}</p>
                                  {evento.ocorrencias.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {evento.ocorrencias.map((ocorrencia) => (
                                        <Badge key={ocorrencia.id} variant="outline" className="text-[10px]">
                                          {ocorrencia.codigo ?? "Sem código"}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      statusTone === "success" && "border-success/40 bg-success/10 text-success",
                                      statusTone === "warning" && "border-warning/40 bg-warning/10 text-warning",
                                      statusTone === "destructive" && "border-destructive/40 bg-destructive/10 text-destructive",
                                      statusTone === "secondary" && "border-muted-foreground/20 bg-muted text-muted-foreground"
                                    )}
                                  >
                                    {evento.codigoRespostaProcessamento ?? evento.statusProcessamento ?? "Sem status"}
                                  </Badge>
                                  <p className="max-w-[320px] text-xs text-muted-foreground">
                                    {evento.descricaoRespostaProcessamento ?? "-"}
                                  </p>
                                  {evento.ocorrencias.length > 0 && (
                                    <div className="rounded-md border border-destructive/20 bg-destructive/5 p-2 text-xs text-muted-foreground">
                                      {evento.ocorrencias.map((ocorrencia) => (
                                        <div key={ocorrencia.id} className="mb-1 last:mb-0">
                                          <span className="font-medium text-foreground">
                                            {ocorrencia.codigo ?? "Sem código"}:
                                          </span>{" "}
                                          {ocorrencia.descricao ?? "Ocorrência sem descrição"}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  <p className="font-mono text-xs">{evento.nrRecibo ?? "-"}</p>
                                  {evento.hashRecibo && (
                                    <p className="max-w-[220px] truncate text-[11px] text-muted-foreground">{evento.hashRecibo}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{formatDateTime(evento.dhProcessamento)}</TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {pending && lote.eventosDetalhados.length === 0 && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <TriangleAlert className="h-4 w-4" />
                    O lote ainda não terminou de processar no eSocial. Use a reconsulta depois de alguns instantes.
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>
    </div>
  )
}
